import {
  Col,
  Menu,
  MenuItemProps,
  MenuProps,
  Row,
  SubMenuProps,
  Tag,
} from "antd";
import SubMenu from "antd/lib/menu/SubMenu";
import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IRouteConfig } from "../../../routes/routes";
import GraphqlService from "../../../services/graphql/GraphqlService";

import { Authorization, ContextApp, EnumsValues } from "../../../shared";
import { AvatarComponent } from "../HeaderComponent/AvatarComponent";
import "./style.less";

interface IMainMenuProps {
  routes: IRouteConfig[];
}

const ENVTagColor: Record<string, string> = {
  dev: "orange",
  test: "green",
  stage: "#87d068",
};

export const MainMenu = (props: IMainMenuProps): JSX.Element => {
  const { routes } = props;
  const location = useLocation();
  const [isCompact, setIsCompact] = useState(() =>
    window.matchMedia("(max-width: 991px)").matches
  );
  const [compactOpenKeys, setCompactOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    setCompactOpenKeys([]);
  }, [location.pathname]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 991px)");
    const update = () => {
      setIsCompact(media.matches);
      setCompactOpenKeys([]);
    };
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  const { Query, customRequest } = GraphqlService();
  const { functions, maintenanceMode, setMaintenanceMode } =
    useContext(ContextApp);

  const handleCompactOpenChange = (openKeys: string[]): void => {
    const newlyOpened = openKeys.find((key) => !compactOpenKeys.includes(key));
    if (!newlyOpened) {
      setCompactOpenKeys(openKeys);
      return;
    }
    const rootKey = newlyOpened.split("_")[0];
    setCompactOpenKeys(
      openKeys.filter((key) => key.split("_")[0] === rootKey)
    );
  };

  const menuProps: MenuProps = {
    theme: "light",
    mode: isCompact ? "inline" : "horizontal",
    ...(isCompact
      ? {
          openKeys: compactOpenKeys,
          onOpenChange: handleCompactOpenChange,
          onClick: () => setCompactOpenKeys([]),
        }
      : {}),
  };
  const getMaintenanceModeSetting = async (): Promise<void> => {
    try {
      const response = await customRequest({
        query: Query.getAppSettingByKey,
        variables: {
          input: { key: EnumsValues.SettingKeys.MaintenanceMode },
        },
      });

      setMaintenanceMode(response.setting_value === "true");
    } catch (error) {
      //
    }
  };

  useEffect(() => {
    getMaintenanceModeSetting();
  }, []);

  const renderMenuRoutes = useCallback(
    (
      paramRoutes: IRouteConfig[],
      previousIndexes: number[] = []
    ) => {
      return paramRoutes.map((item, index) => {
        if (
          (item.authority &&
            !Authorization.security(functions, item.authority)) ||
          item.hideInMenu
        ) {
          return null;
        }
        if (item.routes) {
          const subMenuProps: SubMenuProps = {};
          if (item.icon) {
            subMenuProps.icon = item.icon;
          }
          subMenuProps.title = item.name;
          const currentIndexes = [...previousIndexes, index];
          return (
            <SubMenu
              key={currentIndexes.join("_")}
              {...subMenuProps}
            >
              {renderMenuRoutes(item.routes, currentIndexes)}
            </SubMenu>
          );
        } else {
          const menuItemProps: MenuItemProps = {};
          if (item.icon) {
            menuItemProps.icon = item.icon;
          }
          return (
            <Menu.Item
              key={[...previousIndexes, index].join("_")}
              {...menuItemProps}
            >
              <Link to={item.path}>{item.name}</Link>
            </Menu.Item>
          );
        }
      });
    },
    [functions]
  );

  return (
    <div className="mainMenuContainer">
      <Row>
        <Col xs={3} className="logoColumn">
          <Link to="/app/home">
            <img
              src="/assets/logo.png"
              alt="logo liderar seguros"
              className="logo"
            />
          </Link>
        </Col>
        <Col xs={16} className="menuColumn">
          <Menu
            className="MainMenu"
            {...menuProps}
            style={{
              display: isCompact ? undefined : "flex",
              justifyContent: isCompact ? undefined : "center",
            }}
          >
            {renderMenuRoutes(routes)}
          </Menu>
        </Col>
        <Col xs={5} className="avatarColumn">
          <AvatarComponent />
        </Col>
      </Row>
      {maintenanceMode && (
        <Tag className="maintenance-tag" color="orange">
          En mantenimiento
        </Tag>
      )}
      {process.env?.REACT_APP_ENV && ENVTagColor[process.env.REACT_APP_ENV] && (
        <Tag className="env-tag" color={ENVTagColor[process.env.REACT_APP_ENV]}>
          {process.env.REACT_APP_ENV}
        </Tag>
      )}
    </div>
  );
};
